import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Search,
  Loader2,
  Trash2,
  Pencil,
  Package,
  TrendingDown,
  Building,
  MoreHorizontal,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const huFmt = new Intl.NumberFormat("hu-HU", { maximumFractionDigits: 2 });
const formatFt = (n) => (n == null ? "—" : `${huFmt.format(n)} Ft`);

const Th = ({ children, className = "" }) => (
  <th className={`whitespace-nowrap px-4 py-3 text-left text-[11px] font-medium uppercase tracking-[0.12em] ${className}`}>{children}</th>
);
const Td = ({ children, className = "", ...rest }) => (
  <td className={`whitespace-nowrap px-4 py-3 ${className}`} {...rest}>{children}</td>
);

export default function SupplierProductsPage() {
  return (
    <div className="space-y-6 px-4 py-6 sm:px-6 lg:px-8">
      <header>
        <div className="flex items-center gap-1.5 text-muted-foreground/80">
          <Package className="h-3.5 w-3.5" strokeWidth={2} />
          <span className="text-[10px] font-semibold uppercase tracking-[0.22em]">
            Beszerzés
          </span>
        </div>
        <h1 className="mt-1.5 text-2xl font-semibold tracking-tight">
          Beszállítói termékek
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Termékek és beszállítói áraik összehasonlítása. A legolcsóbb ár
          automatikusan kiemelt. Beszállítókat a Partnerek modulban tudsz
          rögzíteni.
        </p>
      </header>

      <ProductsTab />
    </div>
  );
}

function ProductsTab() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [openCreate, setOpenCreate] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/supplier-products");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Betöltés sikertelen.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        (p.sku || "").toLowerCase().includes(q) ||
        (p.category || "").toLowerCase().includes(q)
    );
  }, [items, search]);

  const onDelete = async (p) => {
    if (!window.confirm(`Törlöd a "${p.name}" terméket és a hozzá tartozó árakat?`)) return;
    try {
      await api.delete(`/supplier-products/${p._id}`);
      setItems((prev) => prev.filter((x) => x._id !== p._id));
      toast.success("Termék törölve.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Nem sikerült törölni.");
    }
  };

  return (
    <section className="overflow-hidden rounded-xl border border-border bg-[hsl(var(--sipx-elevated))]">
      <header className="flex flex-col gap-3 border-b border-border px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex-1">
          <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
            Termékek
          </p>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {items.length === 0
              ? "Még nincs termék rögzítve."
              : `${items.length} termék`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Keresés…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-9 w-56 pl-8"
              data-testid="product-search"
            />
          </div>
          <Button size="sm" onClick={() => setOpenCreate(true)} data-testid="new-product-button">
            <Plus className="h-4 w-4" /> Új termék
          </Button>
        </div>
      </header>

      {loading ? (
        <div className="flex items-center justify-center px-6 py-14 text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Betöltés…
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
          <div className="grid h-10 w-10 place-items-center rounded-full border border-border bg-background">
            <Package className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
          </div>
          <p className="text-sm font-medium">{search ? "Nincs találat" : "Nincs rögzített termék"}</p>
          <p className="text-xs text-muted-foreground">Adj hozzá termékeket és kösd be a beszállítói árakat.</p>
          {!search && (
            <Button variant="outline" size="sm" className="mt-2 h-8" onClick={() => setOpenCreate(true)}>
              <Plus className="h-3.5 w-3.5" /> Új termék
            </Button>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30 text-muted-foreground">
                <Th>Termék</Th>
                <Th>Kategória</Th>
                <Th>Egység</Th>
                <Th className="text-right">Beszállítók</Th>
                <Th className="text-right">Legolcsóbb (nettó)</Th>
                <Th>Beszállító</Th>
                <Th className="text-right">Ársáv</Th>
                <Th className="pr-4 text-right">Műveletek</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr
                  key={p._id}
                  data-testid={`product-row-${p._id}`}
                  onClick={() => navigate(`/supplier-products/${p._id}`)}
                  className="cursor-pointer border-b border-border last:border-0 transition-colors hover:bg-foreground/[0.025]"
                >
                  <Td>
                    <span className="font-medium">{p.name}</span>
                    {p.sku && <span className="ml-2 text-[11px] text-muted-foreground">{p.sku}</span>}
                  </Td>
                  <Td className="text-muted-foreground">{p.category || "—"}</Td>
                  <Td className="text-muted-foreground">{p.unit || "—"}</Td>
                  <Td className="text-right tabular-nums">{p.price_count || 0}</Td>
                  <Td className="text-right tabular-nums font-medium">
                    {p.cheapest_net != null ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-300">
                        <TrendingDown className="h-3.5 w-3.5" /> {formatFt(p.cheapest_net)}
                      </span>
                    ) : "—"}
                  </Td>
                  <Td>{p.cheapest_supplier_name || <span className="text-muted-foreground/60">—</span>}</Td>
                  <Td className="text-right tabular-nums text-xs">
                    {p.spread_net ? (
                      <span className="text-muted-foreground">
                        +{formatFt(p.spread_net)} <span className="text-muted-foreground/60">({p.spread_pct}%)</span>
                      </span>
                    ) : (
                      <span className="text-muted-foreground/60">—</span>
                    )}
                  </Td>
                  <Td className="pr-4 text-right" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          aria-label="Műveletek"
                          className="ml-auto grid h-8 w-8 place-items-center rounded-md text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground"
                          data-testid={`product-actions-${p._id}`}
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-40">
                        <DropdownMenuLabel>Műveletek</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => setEditing(p)}>
                          <Pencil className="h-4 w-4" /> Szerkesztés
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onClick={() => onDelete(p)}
                        >
                          <Trash2 className="h-4 w-4" /> Törlés
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ProductFormDialog
        open={openCreate || !!editing}
        onOpenChange={(v) => {
          if (!v) { setOpenCreate(false); setEditing(null); }
        }}
        product={editing}
        onSaved={(saved, mode) => {
          if (mode === "create") setItems((prev) => [saved, ...prev]);
          else setItems((prev) => prev.map((p) => (p._id === saved._id ? { ...p, ...saved } : p)));
        }}
      />
    </section>
  );
}

function ProductFormDialog({ open, onOpenChange, product, onSaved }) {
  const [form, setForm] = useState({ name: "", sku: "", unit: "", category: "", description: "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const isEdit = !!product;

  useEffect(() => {
    if (!open) return;
    setErr("");
    if (product) {
      setForm({
        name: product.name || "",
        sku: product.sku || "",
        unit: product.unit || "",
        category: product.category || "",
        description: product.description || "",
      });
    } else {
      setForm({ name: "", sku: "", unit: "", category: "", description: "" });
    }
  }, [open, product]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.name.trim()) return setErr("Név megadása kötelező.");
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        sku: form.sku.trim() || null,
        unit: form.unit.trim() || null,
        category: form.category.trim() || null,
        description: form.description.trim() || null,
      };
      const { data } = isEdit
        ? await api.patch(`/supplier-products/${product._id}`, payload)
        : await api.post("/supplier-products", payload);
      toast.success(isEdit ? "Termék frissítve." : "Termék létrehozva.");
      onSaved(data, isEdit ? "edit" : "create");
      onOpenChange(false);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Mentés sikertelen.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="product-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Termék szerkesztése" : "Új termék"}</DialogTitle>
          <DialogDescription>Add meg a termék alapadatait.</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-3">
          <Field label="Név *">
            <Input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="pl. Coca-Cola 0,5L"
              data-testid="product-name"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Cikkszám">
              <Input
                value={form.sku}
                onChange={(e) => setForm((f) => ({ ...f, sku: e.target.value }))}
                placeholder="pl. CC-05"
              />
            </Field>
            <Field label="Egység">
              <Input
                value={form.unit}
                onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))}
                placeholder="db / kg / liter"
              />
            </Field>
          </div>
          <Field label="Kategória">
            <Input
              value={form.category}
              onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
              placeholder="pl. Italok"
            />
          </Field>
          <Field label="Leírás">
            <Input
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="rövid leírás"
            />
          </Field>
          {err && (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">{err}</div>
          )}
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Mégse</Button>
            <Button type="submit" disabled={saving} data-testid="product-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {isEdit ? "Mentés" : "Létrehozás"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SuppliersTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/suppliers");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Betöltés sikertelen.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const onDelete = async (s) => {
    if (!window.confirm(`Törlöd a "${s.name}" beszállítót és az összes hozzá tartozó árat?`)) return;
    try {
      await api.delete(`/suppliers/${s._id}`);
      setItems((prev) => prev.filter((x) => x._id !== s._id));
      toast.success("Beszállító törölve.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Nem sikerült törölni.");
    }
  };

  return (
    <section className="overflow-hidden rounded-xl border border-border bg-[hsl(var(--sipx-elevated))]">
      <header className="flex flex-col gap-3 border-b border-border px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">Beszállítók</p>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {items.length === 0 ? "Még nincs beszállító rögzítve." : `${items.length} beszállító`}
          </p>
        </div>
        <Button size="sm" onClick={() => setOpenCreate(true)} data-testid="new-supplier-button">
          <Plus className="h-4 w-4" /> Új beszállító
        </Button>
      </header>

      {loading ? (
        <div className="flex items-center justify-center px-6 py-14 text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Betöltés…
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
          <div className="grid h-10 w-10 place-items-center rounded-full border border-border bg-background">
            <Building className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
          </div>
          <p className="text-sm font-medium">Nincs rögzített beszállító</p>
          <Button variant="outline" size="sm" className="mt-2 h-8" onClick={() => setOpenCreate(true)}>
            <Plus className="h-3.5 w-3.5" /> Új beszállító
          </Button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30 text-muted-foreground">
                <Th>Név</Th>
                <Th>Kapcsolattartó</Th>
                <Th>Telefon</Th>
                <Th>E-mail</Th>
                <Th className="pr-4 text-right">Műveletek</Th>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr key={s._id} data-testid={`supplier-row-${s._id}`}
                  className="border-b border-border last:border-0 transition-colors hover:bg-foreground/[0.025]">
                  <Td><span className="font-medium">{s.name}</span></Td>
                  <Td className="text-muted-foreground">{s.contact_name || "—"}</Td>
                  <Td className="text-muted-foreground">{s.phone || "—"}</Td>
                  <Td className="text-muted-foreground">{s.email || "—"}</Td>
                  <Td className="pr-4 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button aria-label="Műveletek"
                          className="ml-auto grid h-8 w-8 place-items-center rounded-md text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground">
                          <MoreHorizontal className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-40">
                        <DropdownMenuLabel>Műveletek</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => setEditing(s)}>
                          <Pencil className="h-4 w-4" /> Szerkesztés
                        </DropdownMenuItem>
                        <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => onDelete(s)}>
                          <Trash2 className="h-4 w-4" /> Törlés
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <SupplierFormDialog
        open={openCreate || !!editing}
        onOpenChange={(v) => { if (!v) { setOpenCreate(false); setEditing(null); } }}
        supplier={editing}
        onSaved={(saved, mode) => {
          if (mode === "create") setItems((prev) => [...prev, saved].sort((a, b) => a.name.localeCompare(b.name)));
          else setItems((prev) => prev.map((s) => (s._id === saved._id ? saved : s)));
        }}
      />
    </section>
  );
}

function SupplierFormDialog({ open, onOpenChange, supplier, onSaved }) {
  const [form, setForm] = useState({ name: "", contact_name: "", email: "", phone: "", address: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const isEdit = !!supplier;

  useEffect(() => {
    if (!open) return;
    setErr("");
    setForm({
      name: supplier?.name || "",
      contact_name: supplier?.contact_name || "",
      email: supplier?.email || "",
      phone: supplier?.phone || "",
      address: supplier?.address || "",
      notes: supplier?.notes || "",
    });
  }, [open, supplier]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.name.trim()) return setErr("Név megadása kötelező.");
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        contact_name: form.contact_name.trim() || null,
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        address: form.address.trim() || null,
        notes: form.notes.trim() || null,
      };
      const { data } = isEdit
        ? await api.patch(`/suppliers/${supplier._id}`, payload)
        : await api.post("/suppliers", payload);
      toast.success(isEdit ? "Beszállító frissítve." : "Beszállító létrehozva.");
      onSaved(data, isEdit ? "edit" : "create");
      onOpenChange(false);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Mentés sikertelen.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="supplier-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Beszállító szerkesztése" : "Új beszállító"}</DialogTitle>
          <DialogDescription>Add meg a beszállító adatait.</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-3">
          <Field label="Cégnév *">
            <Input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="pl. Metro"
              data-testid="supplier-name"
            />
          </Field>
          <Field label="Kapcsolattartó">
            <Input value={form.contact_name} onChange={(e) => setForm((f) => ({ ...f, contact_name: e.target.value }))} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Telefon">
              <Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
            </Field>
            <Field label="E-mail">
              <Input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} type="email" />
            </Field>
          </div>
          <Field label="Cím">
            <Input value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} />
          </Field>
          {err && <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">{err}</div>}
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Mégse</Button>
            <Button type="submit" disabled={saving} data-testid="supplier-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {isEdit ? "Mentés" : "Létrehozás"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <label className="block space-y-1.5">
      <span className="block text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
